import { createSequence } from '@olivierpascal/helpers';
import { Button } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import {
  LiveStreamListItem,
  LiveStreamListItemSkeleton,
} from '~/components/molecules/LiveStreamListItem';
import { Pagination } from '~/components/molecules/Pagination';
import { Table } from '~/components/molecules/Table';
import { usePagination } from '~/hooks/usePagination';
import { useLiveStreamsQuery } from '~/lib/api/modules/LiveStreams/hooks/useLiveStreamsQuery';

const GRID_TEMPLATE_COLUMNS = 'repeat(2, 1fr) repeat(1, min-content)';
const DEFAULT_SKELETON_COUNT = 4;

export type ILiveStreamListProps = IContainerProps & {
  appId: string;
};

export const LiveStreamList: React.FC<ILiveStreamListProps> = (props) => {
  const { sx, appId } = props;
  const { t } = useTranslation();
  const pagination = usePagination();
  const liveStreamsQuery = useLiveStreamsQuery(
    {
      appId,
      body: {
        start: pagination.start,
        limit: pagination.limit,
      },
    },
    {
      enabled:
        Number.isInteger(pagination.start) &&
        Number.isInteger(pagination.limit),
      keepPreviousData: true,
    },
  );

  const liveStreams = liveStreamsQuery.data;
  const isEmpty = !liveStreamsQuery.isLoading && !liveStreams?.list.length;

  return (
    <ContentBlock sx={sx}>
      {liveStreamsQuery.isError ? (
        <ContentBlock.Body first>
          <ErrorMessage error={liveStreamsQuery.error} />
        </ContentBlock.Body>
      ) : isEmpty ? (
        <ContentBlock.Empty title={t('LiveStreamList.empty.title')}>
          <Button href={`/apps/${appId}/liveStreams/create`}>
            {t('LiveStreamList.empty.actionLabel')}
          </Button>
        </ContentBlock.Empty>
      ) : (
        <>
          <Table gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
            {liveStreamsQuery.isLoading || liveStreamsQuery.isPreviousData
              ? createSequence(DEFAULT_SKELETON_COUNT).map((index) => (
                  <LiveStreamListItemSkeleton key={index} first={index === 0} />
                ))
              : liveStreams?.list.map((liveStream, liveStreamIndex) => (
                  <LiveStreamListItem
                    key={liveStream._id}
                    liveStream={liveStream}
                    first={liveStreamIndex === 0}
                  />
                ))}
          </Table>

          <ContentBlock.Footer>
            <Pagination
              pagination={pagination}
              total={liveStreamsQuery.data?.count}
            />
          </ContentBlock.Footer>
        </>
      )}
    </ContentBlock>
  );
};
